import type { CascaderOptionType } from "antd/es/cascader";
import type { ReactElement, ReactNode } from "react";

/** Search primitives shared by the properties-panel cascaders (action picker + symbol picker). */

// Must stay 1 char → 1 char: highlightCascaderKeyword finds offsets in the folded string and slices
// the original one.
const TURKISH_FOLD_MAP: Record<string, string> = {
  Ğ: "g",
  ğ: "g",
  Ü: "u",
  ü: "u",
  Ş: "s",
  ş: "s",
  İ: "i",
  ı: "i",
  Ö: "o",
  ö: "o",
  Ç: "c",
  ç: "c"
};

/**
 * Folds a label or a query into the one form both sides of a comparison must share.
 *
 * Not `KMainFunctions.turkishCharsToEnglish`: its `replace` calls have no `/g`, so only the first
 * occurrence of each letter is rewritten. The picker also applied it to the query alone, never to the
 * labels, so "Sirket" could not match "Şirket".
 */
export function normalizeCascaderSearchText(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  let folded: string = "";
  for (const char of value) {
    folded += TURKISH_FOLD_MAP[char] ?? char;
  }
  // toLowerCase, not toLocaleLowerCase: the latter maps "I"→"ı" under a Turkish system locale.
  return folded.toLowerCase();
}

/**
 * The plain text a cascader option is searched and sorted by.
 *
 * A picker option's `label` is a React ELEMENT wherever the row carries more than its name — a
 * category's icon and action count, an action's middle-truncated name and return-type glyph
 * (KOA-18357). Every call site that builds such an option also stores the bare name in
 * `searchLabel`, and reading it is the whole point: falling through to `value` yields the action
 * definition's GUID, which no query can ever match and which sorts rows in a meaningless order.
 *
 * Shared because five pickers ask the same question — the action picker plus the four nested
 * action lists (If/Then/Else, For Each, Recurring, MQTT Subscribe). The nested four had kept a
 * private copy that predated `searchLabel`, so their search answered "No Data" for every query
 * (measured on platform.kuika.com, 2026-09-01: "Navigate" in an If/Then/Else "+ ADD IF" picker).
 */
export function getCascaderOptionSearchText(option: CascaderOptionType): string {
  if (typeof option.searchLabel === "string" && option.searchLabel !== "") {
    return option.searchLabel;
  }
  if (typeof option.label === "string") {
    return option.label;
  }
  return option.value === undefined || option.value === null ? "" : String(option.value);
}

/** The designer's generated-test-id spelling of a name: no whitespace, lower case. */
function toTestIdSlug(value: string): string {
  return value.replace(/\s+/g, "").toLowerCase();
}

/**
 * True when `needle` is a subsequence of `haystack`, so "gtDok" still finds "getDokumanById". Both
 * arguments must already be normalized.
 *
 * Replaces a per-path `new Fzf(...)`: only its boolean was ever used, and for that its v2 algorithm
 * reduces to this test.
 */
export function matchesCascaderSearch(haystack: string, needle: string): boolean {
  if (!needle) {
    return true;
  }
  if (haystack.includes(needle)) {
    return true;
  }
  let cursor: number = 0;
  for (const char of needle) {
    const found: number = haystack.indexOf(char, cursor);
    if (found === -1) {
      return false;
    }
    cursor = found + char.length;
  }
  return true;
}

/**
 * Splits `str` into plain text and `<span>`-wrapped runs of `keyword`, for antd's
 * `showSearch.render`. Case- and Turkish-insensitive; a fuzzy-only match highlights nothing.
 */
export function highlightCascaderKeyword(
  str: string,
  keyword: string,
  prefixCls: string | undefined = ""
): (string | ReactElement)[] {
  if (!str) {
    return [];
  }
  const haystack: string = normalizeCascaderSearchText(str);
  const needle: string = normalizeCascaderSearchText(keyword);
  if (!needle) {
    return [str];
  }

  const parts: (string | ReactElement)[] = [];
  let lastIndex: number = 0;
  let index: number = haystack.indexOf(needle);

  while (index !== -1) {
    if (index > lastIndex) {
      parts.push(str.substring(lastIndex, index));
    }
    parts.push(
      <span className={`${prefixCls}-menu-item-keyword`} key={`keyword_${index}`}>
        {str.substring(index, index + needle.length)}
      </span>
    );
    lastIndex = index + needle.length;
    index = haystack.indexOf(needle, lastIndex);
  }

  if (lastIndex < str.length) {
    parts.push(str.substring(lastIndex));
  }

  return parts;
}

/** Summary of a cascader search, for the popup header's "Search Results (n)" field badge. */
export interface ICascaderSearchResultCount {
  /** How many rows the popup actually lists. */
  shown: number;
  /** True when more matched than the render cap, so the badge must read "n+" and not a total. */
  truncated: boolean;
}

/**
 * Every path a cascader search can offer, in antd's own order.
 *
 * Mirrors the private `flattenTree` in `antd/lib/cascader`: with `changeOnSelect` off — which is how
 * both pickers mount their Cascader — only LEAF paths are searchable, so a category row never
 * becomes a result. Reproduced rather than imported because antd exports neither the function nor
 * the filtered set, and a count that did not come from exactly the set antd renders would drift.
 */
export function flattenCascaderLeafPaths(
  options: CascaderOptionType[],
  ancestor: CascaderOptionType[] = []
): CascaderOptionType[][] {
  const paths: CascaderOptionType[][] = [];
  for (const option of options) {
    const path: CascaderOptionType[] = [...ancestor, option];
    const children: CascaderOptionType[] = option.children ?? [];
    if (children.length === 0) {
      paths.push(path);
    } else {
      paths.push(...flattenCascaderLeafPaths(children, path));
    }
  }
  return paths;
}

/**
 * Counts what the search list will show, stopping one match past `limit`.
 *
 * `limit` is the cap antd was given on rendered results (its own default is 50). Counting one past
 * it is what separates "exactly `limit` matched" from "more did"; stopping there keeps the badge
 * from costing more than antd's own scan, which short-circuits at the same cap.
 */
export function countCascaderSearchResults(
  options: CascaderOptionType[],
  matches: (path: CascaderOptionType[]) => boolean,
  limit: number
): ICascaderSearchResultCount {
  let matched: number = 0;
  for (const path of flattenCascaderLeafPaths(options)) {
    if (!matches(path)) {
      continue;
    }
    matched += 1;
    if (matched > limit) {
      return { shown: limit, truncated: true };
    }
  }
  return { shown: matched, truncated: false };
}

/**
 * One search hit of the action picker, as a breadcrumb: event / category / action.
 *
 * The segments are MARKED rather than concatenated, because a result row and a browse row answer
 * different questions and must not look alike. A browse row IS its category — one thing, so it
 * tints as one. A result row is a PATH ending in the thing you came for, so the path stays context
 * (muted, and it never tints) while the last segment carries the emphasis and the hover accent.
 * The three classes and their rules are shared with the symbol picker's rows, so both pickers look
 * identical while searching (styling.scss, `.kuika-action-search-result__*`).
 *
 * Lives here, beside the highlighter it calls, so a test can exercise it without mounting the
 * picker; `action-picker.tsx` hands it straight to antd's `showSearch.render`.
 *
 * `labelKey` is antd's configured label field. A segment whose label is an ELEMENT — the category
 * row, with its icon — renders that element unless it also carries a plain `searchLabel`, so the
 * icon survives; highlighting then applies to string segments only, which is the pre-existing
 * behaviour and the reason `searchLabel` exists.
 */
export function renderActionPickerSearchRow(
  inputValue: string,
  path: CascaderOptionType[],
  prefixCls: string | undefined,
  labelKey: string
): ReactElement {
  const lastIndex: number = path.length - 1;
  const nodes: ReactElement[] = [];
  const rowName: string = lastIndex >= 0 ? getCascaderOptionSearchText(path[lastIndex]) : "";

  path.forEach((option: CascaderOptionType, index: number): void => {
    const rawLabel: unknown = option[labelKey];
    const label: unknown =
      typeof rawLabel !== "string" && typeof option.searchLabel === "string" ? option.searchLabel : rawLabel;
    const isMatch: boolean =
      typeof label === "string" && normalizeCascaderSearchText(label).includes(normalizeCascaderSearchText(inputValue));
    const content: ReactNode =
      typeof label === "string" && isMatch
        ? highlightCascaderKeyword(label, inputValue, prefixCls)
        : (label as ReactNode);

    // A sibling, not a child of the segment: a segment's text stays exactly its own name, and the
    // separator keeps its own colour while the value inherits the row's accent.
    if (index > 0) {
      nodes.push(
        <span key={`sep_${index}`} className="kuika-action-search-result__separator">
          {" / "}
        </span>
      );
    }
    nodes.push(
      <span
        key={`seg_${index}_${String(option.value)}`}
        className={index === lastIndex ? "kuika-action-search-result__value" : "kuika-action-search-result__context"}
      >
        {content}
      </span>
    );
  });

  // Named after the thing the row actually offers — the last path segment. A search hit renders
  // its label as an ELEMENT (middle-truncated name, category icon and count), so its text is split
  // across spans and cannot be matched as a whole; the E2E suite's `getByTestId` policy needs a
  // hook that survives that, and the `<name without spaces>` shape is the one the rest of the
  // designer already uses for generated test-ids.
  return (
    <span className="kuika-action-search-result" data-testid={`actionSearchResult-${toTestIdSlug(rowName)}`}>
      {nodes}
    </span>
  );
}
